import type { QueryClient } from "@tanstack/react-query";
import { queryOptions } from "@tanstack/react-query";
import type { UIMessage } from "ai";
import { getChatHistoryApi } from "./hooks/use-chat-history";
import { chatHistoryKeys } from "./query-keys";

const pendingChatRouteSeeds = new Set<string>();

function getPendingChatRouteSeedKey(viewerId: string, chatId: string) {
  return `${viewerId}:${chatId}`;
}

export function chatHistoryQueryOptions({
  baseUrl,
  viewerId,
}: {
  baseUrl: string;
  viewerId: string;
}) {
  const api = getChatHistoryApi(baseUrl);
  return queryOptions({
    queryKey: chatHistoryKeys.list(viewerId),
    queryFn: api.fetchHistory,
    staleTime: 0,
    retry: false,
  });
}

export function chatMessagesQueryOptions({
  baseUrl,
  viewerId,
  chatId,
}: {
  baseUrl: string;
  viewerId: string;
  chatId: string;
}) {
  const api = getChatHistoryApi(baseUrl);
  return queryOptions({
    queryKey: chatHistoryKeys.messages(viewerId, chatId),
    queryFn: () => api.fetchMessages(chatId),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

export function seedPendingChatMessagesRoute({
  baseUrl,
  chatId,
  queryClient,
  viewerId,
}: {
  baseUrl: string;
  chatId: string;
  queryClient: QueryClient;
  viewerId: string;
}) {
  const query = chatMessagesQueryOptions({ baseUrl, viewerId, chatId });
  queryClient.setQueryData<UIMessage[]>(query.queryKey, []);
  pendingChatRouteSeeds.add(getPendingChatRouteSeedKey(viewerId, chatId));
}

export function loadChatMessagesForRoute({
  baseUrl,
  chatId,
  queryClient,
  viewerId,
}: {
  baseUrl: string;
  chatId: string;
  queryClient: QueryClient;
  viewerId: string;
}) {
  const query = chatMessagesQueryOptions({ baseUrl, viewerId, chatId });
  const seedKey = getPendingChatRouteSeedKey(viewerId, chatId);

  if (
    pendingChatRouteSeeds.delete(seedKey) &&
    queryClient.getQueryData(query.queryKey) !== undefined
  ) {
    return queryClient.ensureQueryData(query);
  }

  return queryClient.fetchQuery({ ...query, staleTime: 0 });
}
